import {useEffect} from 'react';
import getColor from '../functions/getColor';
import setGlobalState from '../redux/setGlobalState';
import {useSelector} from 'react-redux';
import {getFirstDayInMonth, getLastDayInMonth, combineDateTime} from '../library/dates';
import {addTicket, updateTicket} from '../redux/tickets/ticketsActions';
import {useTranslation} from 'react-i18next';
import {getValidationSchema} from '../pages/tickets/ticketsPopUp/functions/validationSchema';
import {getInitialValues} from '../pages/tickets/ticketsPopUp/functions/TicketInitialValues';
import {defaultImage} from '../assets/symbols/images/defaultImage';
import {bounceMessage} from '../layout/userMessages/UserMessage';
import {getMessages, hasError} from '../pages/tickets/tickets/getErrorMessage';
import {getOverlappingTicketNames, getValidityGapErrorPayload} from '../pages/tickets/ticketsPopUp/basicTicket/ticketValidity/validityValidation';
import {hasAccountAccess} from '../functions/permission';

export default function useTicket() {
    const {t} = useTranslation('ticket');

    const tickets = useSelector((s) => s.tickets?.tickets);
    const ui = useSelector((s) => s.ui);
    const currentColor = ui?.colorsPicker?.currentColor;
    const event = useSelector((s) => s.event);
    const ticket = useSelector((s) => s.tickets?.currentTicket);

    useEffect(() => {
        const color = getColor(currentColor, tickets);
        setGlobalState('tickets.currentTicket', {color});
    }, []);

    const save = (_changedValues,values) => {
        let newValues = {...values};
        if(values.draft){
            newValues.active=0;
        }
        if(values.imgData !== defaultImage) {
            newValues.imgData = values.imgData;
        }
        if(currentColor) newValues.color = currentColor;
        newValues.description = JSON.stringify(values.description);
        newValues.typeTicket = event.type;
        newValues.ticketFieldCustom = values.courseFields;
        newValues.payments = {...newValues.payments};

        // Handle validity data (nested structure for server)
        const ticketValidityPermission = hasAccountAccess('ticket validity');
        if(ticketValidityPermission) {
            const hasStartValidity = values.startDateActive && values.startDate != null && values.startTime != null;
            const hasEndValidity = values.endDateActive && values.endDate != null && values.endTime != null;
            newValues.validity = {
                validity: 'date',
                startDateActive: values.startDateActive ? 1 : 0,
                startDate: hasStartValidity ? combineDateTime(values.startDate, values.startTime) : null,
                endDateActive: values.endDateActive ? 1 : 0,
                endDate: hasEndValidity ? combineDateTime(values.endDate, values.endTime) : null,
                preActive: values.displayBeforeSale ? 1 : 0,
                postActive: values.displayAfterSale ? 1 : 0
            };
        }

        // Handle crowd.isMemberLimit logic
        if(newValues.crowd ) {
            if(newValues.crowd.isMemberLimit) {
                newValues.crowd.limit = -1;
            }
        }

        if(newValues.course.startDate&&newValues.course.endDate)
            newValues.course = {
                ...newValues.course,
                startDate: getFirstDayInMonth(newValues.course.startDate),
                endDate: getLastDayInMonth(newValues.course.endDate),

            };
        newValues.location = {
            ...newValues.location,
            skipHolidays: [newValues.location.skipHolidays],
            draft:values.draft,
            hideGuide:values.hideGuide,
            hideScheduled:values.hideScheduled,
        };
        newValues.amountAvailable = values.amount;
        // if(newValues.preSale && (newValues.preSale.ableFrom ||newValues.preSale.discount!==0)){
        //     newValues.preSale =[newValues.preSale];
        //     newValues.prices=newValues.prices.map(itemPrice=>{
        //         if(!itemPrice.preSale.length)
        //             return {...itemPrice,preSale:[{tpsid:newValues.preSale[0].tpsid||0,discount:newValues.preSale[0].discount}]};
        //         return itemPrice;
        //     });
        //
        // }
        // else
        //     newValues.preSale =null;
        if(Array.isArray(newValues.extras)) {
            newValues.extras = newValues.extras.map((extra, index) => ({
                ...extra,
                active: extra.active === false ? 0 : extra.active === true ? 1 : extra.active,
                weight: index,
            }));
        }

        const executeSave = (valuesToSave) => {
            if(!ticket?.tid) {
                addTicket(valuesToSave);
                if(!tickets.length && event.type === 'course')
                    setGlobalState('ui.courses.feedback', true);
            } else {
                updateTicket(ticket?.tid, valuesToSave).then(({errors})=>{
                    const error = hasError(errors);
                    if(error) {
                        bounceMessage(getMessages(t, errors), 'warning');
                    }
                });
            }
        };

        if(newValues.validity) {
            const overlapNames = getOverlappingTicketNames(tickets, ticket?.tid ?? null, newValues.validity);
            if(overlapNames.length) {
                bounceMessage(
                    t('validityOverlapError', {ticketNames: overlapNames.join(', ')}),
                    'warning',
                    {text: t('confirm'), onClick: () => executeSave(newValues)},
                    {text: 'cancelBtn'}
                );
                return;
            }
            const gapKey = getValidityGapErrorPayload(tickets, ticket?.tid ?? null, newValues.validity);
            if(gapKey) {
                bounceMessage(
                    t('validityGapError'),
                    'warning',
                    {text: t('confirm'), onClick: () => executeSave(newValues)},
                    {text: 'cancelBtn'}
                );
                return;
            }
        }

        executeSave(newValues);
    };

    const mergedInitialValues = (values) => {
        return {
            ...values.details,
            ...values.extras,
            ...values.payments,
            ...values.customFields,
            ...values.validity,
        };
    };

    const getGeneralInitialValues = () => getInitialValues(ticket || {}, t, event?.settings?.clubs?.couponTicketClubs, tickets);

    return {
        save,
        validationSchema: getValidationSchema(t),
        generalInitialValues: getGeneralInitialValues(),
        mergedInitialValues
    };
}
